<template>
    <div class="row">
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>

        <!-- <div v-if="loadingResults" class="w-100 min-height-500">
            <busy-spinner :text="'Loading ' + workflowItemName + '...'"></busy-spinner>
        </div> -->
        
        <div class="col-3">
            <div v-if="availableWfSpecs.length < 1" class="w-100">
                <busy-spinner v-if="loadingSpecOptions"></busy-spinner>
                <div v-else class="text-muted special-text">No Issue Types Available</div>
            </div>
            <div v-if="hasMultipleWfSpecs" class="w-100">
                <b-form-group
                    label="Issue Type"
                    label-class="font-weight-bold font-size-100 text-primary-darker"
                    class="mb-4"
                >
                    <b-form-select
                        :value="selectedWfSpecId"
                        @input="handleWfSpecSelection"
                        :options="availableWfSpecs"
                        value-field="value"
                        text-field="label"
                        size="lg"
                        :disabled="loadingSpecOptions"
                    >
                        <template #first>
                            <b-form-select-option :value="null" disabled>-- Select one --</b-form-select-option>
                        </template>
                    </b-form-select>
                </b-form-group>
            </div>
            <div v-if="hasSelectedWfSpec && !loadingResults" class="">
                <div class="font-weight-bold font-size-100 text-primary-darker mb-2">Filters</div>
                <div class="d-flex gap-3 mb-3">
                    <b-button block :variant="isResetButtonDisabled ? 'outline-soft-primary' : 'primary'" :disabled="isResetButtonDisabled" @click="resetAllFilters">Reset Filters</b-button>
                </div>
                <div 
                    class="border rounded-lg mb-3 analytics-select-filter-wrapper"
                    :class="isAnyDateFilterApplied ? 'filter-applied' : ''"
                >
                    <div class="px-3 py-2 font-size-100 font-weight-600 d-flex align-items-center justify-content-between">
                        <div>
                            Dates
                            <span 
                                v-if="isAnyDateFilterApplied"
                                class="ml-2 text-accent-darker font-size-70 font-weight-600 text-uppercase"
                            >Filtered</span>
                        </div>
                        <div>
                            <b-button size="sm" variant="gray-primary" :disabled="!isAnyDateFilterApplied" @click="resetDateFilters(true)">Reset</b-button>
                        </div>
                    </div>
                    <div class="d-flex gap-2 align-items-end justify-content-start px-3 py-2">
                        <div>
                            <div class="hot-edit-label">From</div>
                            <div class="p-field">
                                <label for="start-datepicker" class="sr-only">Start Date</label>
                                <Calendar 
                                    id="start-datepicker" 
                                    :value="localStartDate" 
                                    :view="datePickerView"
                                    :dateFormat="datePickerFormat" 
                                    :yearNavigator="true" 
                                    :minDate="overallMinDate"
                                    :maxDate="overallMaxDate"
                                    :manualInput="false"
                                    :placeholder="`Starting ${dateRangeMode}`"
                                    :disabled="loadingResults"
                                    :showWeek="dateRangeMode == 'day'"
                                    inputClass="p-inputtext-sm"
                                    @date-select="handleStartDateSelect"
                                >
                                    <template #date="{date}">{{date.day}}</template>  <!-- had to manually add the slots default content for some reason with prime-vue components -->
                                </Calendar>
                            </div>
                        </div>
                        <div class="">
                            <div class="hot-edit-label">to</div>
                            <div class="">
                                <label for="end-datepicker" class="sr-only">End Date</label>
                                <Calendar 
                                    id="end-datepicker" 
                                    :value="localEndDate" 
                                    :view="datePickerView"
                                    :dateFormat="datePickerFormat" 
                                    :yearNavigator="true" 
                                    :minDate="overallMinDate"
                                    :maxDate="overallMaxDate"
                                    :manualInput="false"
                                    :placeholder="`Ending ${dateRangeMode}`"
                                    :disabled="loadingResults"
                                    :showWeek="dateRangeMode == 'day'"
                                    inputClass="p-inputtext-sm"
                                    @date-select="handleEndDateSelect"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div 
                    class="rounded-lg border overflow-hidden"
                >
                    <div 
                        class="overflow-hidden analytics-select-filter-wrapper"
                        v-for="(selectField, idx) in selectFieldsForFilters"
                        :key="selectField.key"
                        :class="[
                            isSelectFilterApplied(selectField.key) ? 'filter-applied shadow-sm' : '',
                            idx > 0 ? 'border-top' : '',
                        ]"
                    >
                        <b-button 
                            v-b-toggle="`select-filter-${selectField.key}`"
                            variant="zone-secondary"
                            class="px-3 py-2 text-left font-size-100 font-weight-600 d-flex align-items-center justify-content-between analytics-select-filter-toggle-button"
                        >
                            <div>
                                {{ selectField.label }}
                                <span 
                                    v-if="isSelectFilterApplied(selectField.key)"
                                    class="ml-2 text-accent-darker font-size-70 font-weight-600 text-uppercase"
                                >Filtered</span>
                            </div>
                            <b-icon icon="chevron-down" class="when-closed text-muted"></b-icon>
                            <b-icon icon="chevron-up" class="when-open text-muted"></b-icon>
                        </b-button>
                        <b-collapse :id="`select-filter-${selectField.key}`" class="bg-white">
                            <analytics-select-filter
                                :title="selectField.label"
                                :options="selectField.options"
                                class="px-3 py-2"
                                ref="selectFilters"
                                @updated="handleSelectFilterUpdated(selectField.key, $event)"
                                @reset="handleSelectFilterReset(selectField.key)"
                            ></analytics-select-filter>
                        </b-collapse>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="col-9">
            <div v-if="hasSelectedWfSpec && loadingResults" key="loadingResults" class="w-100 min-height-500">
                <busy-spinner :text="'Loading ' + workflowItemName + '...'"></busy-spinner>
            </div>
            <div v-else-if="hasSelectedWfSpec && hasItems" key="resultsLoaded" class="">
                <!-- TOP GAUGES -->
                <div
                    class="w-100 border rounded-lg bg-white d-flex gap-4 justify-content-around mb-3"
                >
                    <div class="d-flex gap-5 justify-content-around align-items-center">
                        <div class="hot-edit-label">Total</div>
                        <div class="font-size-200 font-weight-bold">{{ items.length }}</div>
                    </div>
                    <div class="d-flex gap-5 justify-content-around align-items-center">
                        <div class="hot-edit-label">Filtered</div>
                        <div class="font-size-200 font-weight-bold">{{ itemsFiltered.length }}</div>
                    </div>
                </div>
                <!-- CHARTS! -->
                <analytics-chart
                    v-for="cDef in chartDefs"
                    :key="`${cDef.chartKey}${cDef.xKey}${cDef.seriesKey}`"
                    v-bind="cDef"
                    :items="itemsFiltered"
                    :field-list="fieldListForCharts"
                    :item-name="workflowItemName"
                    class="w-100 mb-3"
                    @show-detail="handleChartShowDetail"
                ></analytics-chart>
                <!-- <div>
                    <b-button @click="getCombined">Get Combined</b-button>
                    <pre>{{ combinedData }}</pre>
                </div> -->
            </div>
            <div v-else-if="hasSelectedWfSpec" key="noResults" class="">
                No results
            </div>
        </div>

        <b-modal
            v-model="detailModalShowing"
            :title="detailTitle"
            centered
            scrollable
            hide-footer
            @hidden="handleDetailModalHidden"
        >
            <div class="d-flex gap-4 align-items-baseline">
                <h6>{{ detailItems.length }} items total</h6>
                <span class="text-muted font-weight-500 font-size-80 text-uppercase">
                    (showing items {{ showingDetailItemsStart }} - {{showingDetailItemsEnd}})
                </span>
            </div>
            <b-table
                :items="detailItems"
                :fields="detailTableFields"
                primary-key="id"
                small
                :current-page="detailCurrentPage"
                :per-page="detailPerPage"
            >
                <template #cell(title)="data">
                    <b-link :href="data.item.urls.internal_detail || data.item.permalink_url" target="_blank">{{ data.value }}</b-link>
                </template>
            </b-table>
            <b-pagination
                v-model="detailCurrentPage"
                :total-rows="detailItems.length"
                :per-page="detailPerPage"
                align="end"
                size="sm"
                class="my-0"
            ></b-pagination>
            <!-- <div class="">
                <div 
                    v-for="dItem in detailItems"
                    :key="dItem.id"
                    class=""
                >
                    <b-link :href="dItem.urls.internal_detail">{{ dItem.title }} - {{ dItem.vendor.name }}</b-link>
                </div>
            </div> -->
        </b-modal>
        
    </div>
</template>

<script>
import { errorHandling, tenantInfoMixin } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service.js';
//import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import Calendar from 'primevue/calendar';
//import Listbox from 'primevue/listbox';
//import Chips from 'primevue/chips';
//import Checkbox from 'primevue/checkbox';
import { 
    DateTime, 
    Info, 
    //Interval, 
    //Duration,
} from 'luxon';
import { FormatDateHelper } from '../../js/filters-helpers.js';
import padStart from 'lodash/padStart';
import AnalyticsChart from './AnalyticsChart.vue';
import AnalyticsSelectFilter from './AnalyticsSelectFilter.vue';


const zeroPad = (numberOrString, length) => {
    // pass in a number (should be int) or a string, this will pad with zeros on left to up length
    const theString = String(numberOrString);
    return padStart(theString, length, '0');
}

const getMonthName = (monthInt) => {
    // pass in integer month (1-12) this returns the full name of month:  1 => 'January'
    // the Info.months() method can take a bunch of options... worry about that later
    return Info.months()[monthInt - 1];  // must subtract 1, monthInt is 1-based, Info.months() is zero-based
}

// for each bin, have binKey (number, so its sortable?) and binName - for display
// also add 'start' and 'end' DateTime?

class YearBin {
    constructor({
        year,  // pass in 4-digit number as year
    }) {
        this.year = year;
        //this.binKey = year;
        this.binKey = YearBin.makeBinKey(year);
        this.binName = String(year);
    }
    get itemBinProperty() {
        // this is so we have it available on instances AND classes
        return this.constructor.itemBinProperty;  // this *should* allow overriding in subclasses -- see JS classes docs
    }
    static itemBinProperty = 'dateBins_year';  // each item should have property with this name -- DEFINE HERE ONLY
    static makeBinKey(year) {
        return year;  // super simple, but doing it for consistency with others
    }
    static getBinKeyFromDateTime(dt) {
        // STATIC METHOD - pass in a DateTime instance, this returns the binKey to use 
        // putting it here just to keep things close, since you need binKeys in multiple places
        return YearBin.makeBinKey(dt.year);
    }
}
const makeYearBins = (start, end) => {
    // pass in a start and end DateTime, return ARRAY of YearBin instances
    if (end <= start) {
        throw Error('When making bins - end date must be AFTER start date');
    }
    const startYear = start.year;  // number
    const endYear = end.year;  // number
    const bins = [];
    let yr = startYear;
    while (yr <= endYear) {  // inclusive of end year
        const bin = new YearBin({year: yr});
        bins.push(bin);
        yr++;
    }
    return bins;
}

class QuarterBin {
    constructor({
        year,  // 4 digit number as year
        quarter,  // 1, 2, 3, 4 as number
    }) {
        this.year = year;
        this.quarter = quarter;
        //this.binKey = Number(`${year}${zeroPad(quarter, 2)}`);
        this.binKey = QuarterBin.makeBinKey(year, quarter);
        this.binName = `${year} - Q${quarter}`;
    }
    get itemBinProperty() {
        return this.constructor.itemBinProperty;  // this *should* allow overriding in subclasses -- see JS classes docs
    }
    static itemBinProperty = 'dateBins_quarter';  // each item should have property with this name -- DEFINE HERE ONLY
    static makeBinKey(year, quarter) {
        return Number(`${year}${zeroPad(quarter, 2)}`);
    }
    static getBinKeyFromDateTime(dt) {
        // STATIC METHOD - pass in a DateTime instance, this returns the binKey to use 
        // putting it here just to keep things close, since you need binKeys in multiple places
        return QuarterBin.makeBinKey(dt.year, dt.quarter);
    }
}
const makeQuarterBins = (start, end) => {
    // pass in a start and end DateTime, return ARRAY of QuarterBin instances
    if (end <= start) {
        throw Error('When making bins - end date must be AFTER start date');
    }
    const startYear = start.year;  // number
    const startQtr = start.quarter;  // number, 1-4
    const endYear = end.year;  // number
    const endQtr = end.quarter;  // number, 1-4
    const bins = [];
    let yr = startYear;
    let qtr = startQtr;
    while (yr <= endYear) {  // inclusive of end year
        let stopQtr = (yr === endYear) ? endQtr : 4;  // if we are in final year of interval, stop at endQtr, otherwise run through Q4
        while (qtr <= stopQtr) {
            const bin = new QuarterBin({year: yr, quarter: qtr});
            bins.push(bin);
            qtr++;
        }
        // once quarters are exhausted, reset qtr to 1, increment yr
        qtr = 1;
        yr++;
    }
    return bins;
}

class MonthBin {
    constructor({
        year,  // 4 digit number as year
        month,  // 1-12 as number
    }) {
        this.year = year;
        this.month = month;
        this.binKey = MonthBin.makeBinKey(year, month);
        this.binName = `${getMonthName(month)} ${year}`;
    }
    get itemBinProperty() {
        return this.constructor.itemBinProperty;  // this *should* allow overriding in subclasses -- see JS classes docs
    }
    static itemBinProperty = 'dateBins_month';  // each item should have property with this name -- DEFINE HERE ONLY
    static makeBinKey(year, month) {
        return Number(`${year}${zeroPad(month, 2)}`);
    }
    static getBinKeyFromDateTime(dt) {
        // STATIC METHOD - pass in a DateTime instance, this returns the binKey to use 
        // putting it here just to keep things close, since you need binKeys in multiple places
        return MonthBin.makeBinKey(dt.year, dt.month);
    }
}
const makeMonthBins = (start, end) => {
    // pass in a start and end DateTime, return ARRAY of MonthBin instances
    if (end <= start) {
        throw Error('When making bins - end date must be AFTER start date');
    }
    const startYear = start.year;  // number
    const startMonth = start.month;  // number, 1-12
    const endYear = end.year;  // number
    const endMonth = end.month;  // number, 1-12
    const bins = [];
    let yr = startYear;
    let month = startMonth;
    while (yr <= endYear) {  // inclusive of end year
        let stopMonth = (yr === endYear) ? endMonth : 12;  // if we are in final year of interval, stop at endMonth, otherwise run through 12
        while (month <= stopMonth) {
            const bin = new MonthBin({year: yr, month: month});
            bins.push(bin);
            month++;
        }
        // once months are exhausted, reset month to 1, increment yr
        month = 1;
        yr++;
    }
    return bins;
}

class WeekBin {
    constructor({
        weekYear,  // is ISO week year as int  (2022)
        weekNumber,  // the ISO week number as int  (15)
    }) {
        this.weekYear = weekYear;
        this.weekNumber = weekNumber;
        //this.binKey = Number(`${weekYear}${zeroPad(weekNumber, 2)}`);
        this.binKey = WeekBin.makeBinKey(weekYear, weekNumber);
        this.binName = `${weekYear}-W${zeroPad(weekNumber, 2)}`;  // should we pad the W04 part?  I think it looks better
    }
    get itemBinProperty() {
        return this.constructor.itemBinProperty;  // this *should* allow overriding in subclasses -- see JS classes docs
    }
    static itemBinProperty = 'dateBins_week';  // each item should have property with this name -- DEFINE HERE ONLY
    static makeBinKey(weekYear, weekNumber) {
        return Number(`${weekYear}${zeroPad(weekNumber, 2)}`);
    }
    static getBinKeyFromDateTime(dt) {
        // STATIC METHOD - pass in a DateTime instance, this returns the binKey to use 
        // putting it here just to keep things close, since you need binKeys in multiple places
        return WeekBin.makeBinKey(dt.weekYear, dt.weekNumber);
    }
}
const makeWeekBins = (start, end) => {
    // pass in a start and end DateTime, return ARRAY of WeekBin instances
    if (end <= start) {
        throw Error('When making bins - end date must be AFTER start date');
    }
    const startYear = start.weekYear;  // number - the ISO week year
    const startWeek = start.weekNumber;  // number - the ISO week number
    const endYear = end.weekYear;  // number - the ISO week year
    const endWeek = end.weekNumber;  // number - the ISO week number
    const bins = [];
    let yr = startYear;  // will increment this as we go
    let week = startWeek;
    while (yr <= endYear) {  // inclusive of end year
        let stopWeek = (yr === endYear) ? endWeek : (DateTime.utc(yr, 2).weeksInWeekYear);  // if we are in final year of interval, stop at endWeek, otherwise make a DateTime for the current yr we're iterating through, and get the number of ISO weeks in that year
        // we are checking the weeksInWeekYear based on Feb 1 of that year... checking on Jan 1 (only specifying year) gave wonky results!
        //console.log(`stopWeek for yr ${yr} is ${stopWeek} - endWeek = ${endWeek} - endYear = ${endYear}`);
        while (week <= stopWeek) {
            const bin = new WeekBin({weekYear: yr, weekNumber: week});
            bins.push(bin);
            week++;
        }
        // once weeks are exhausted, reset week to 1, increment yr
        week = 1;
        yr++;
    }
    return bins;
}

class DayBin {
    constructor({
        year,  // 4 digit number as year
        month,  // 1-12 as number
        day,  // 1-31 as number
    }) {
        this.year = year;
        this.month = month;
        this.day = day;
        this.binKey = DayBin.makeBinKey(year, month, day);
        const luxonDT = DateTime.fromObject({year, month, day});
        this.binName = luxonDT.toLocaleString();  // since this is a full date, just use Luxon to format
        // binName is the friendly name to use for display
    }
    get itemBinProperty() {
        return this.constructor.itemBinProperty;  // this *should* allow overriding in subclasses -- see JS classes docs
    }
    static itemBinProperty = 'dateBins_day';  // each item should have property with this name -- DEFINE HERE ONLY
    static makeBinKey(year, month, day) {
        return Number(`${year}${zeroPad(month, 2)}${zeroPad(day, 2)}`);
    }
    static getBinKeyFromDateTime(dt) {
        // STATIC METHOD - pass in a DateTime instance, this returns the binKey to use 
        // putting it here just to keep things close, since you need binKeys in multiple places
        return DayBin.makeBinKey(dt.year, dt.month, dt.day);
    }
}
const makeDayBins = (start, end) => {
    // pass in a start and end DateTime, return ARRAY of DayBin instances
    if (end <= start) {
        throw Error('When making bins - end date must be AFTER start date');
    } 
    const bins = [];
    let dt = start;
    while (dt <= end) {  // inclusive of end day
        const bin = new DayBin({year: dt.year, month: dt.month, day: dt.day});
        bins.push(bin);
        dt = dt.plus({ days: 1 });  // increment and continue looping...
    }
    return bins;
}

class AnalyticsItem {
    constructor({
        id,  // required
        title,  // required
        created,  // required - WE WILL ADJUST BASED ON TENANT TZ FROM tenantInfo.settings.timezone
        modified,
        tenant_tz_name,  // get from tenantInfo.settings.timezone, not from backend object
        sm_state,  // required
        owner,
        owner_id,
        vendor,
        vendor_id,
        metadata,  // for DWF workflows 
        custom_data,  // for NEW workflows  (maybe _cf_data?)
        urls,
        workflow_run_number,  // for DWF workflows - but not used? 
        workflow_spec_id,  // for DWF workflows - but not used?
        ...others
    }) {
        this.id = id;  // required
        this.title = title;  // required
        this.created = DateTime.fromISO(created);
        this.created_as_tenant_tz = DateTime.fromISO(created).setZone(tenant_tz_name);  // USE THIS FOR BINNING - use { setZone: true } to prevent it from converting to UTC, which is needed so we can get correct parts of date in tenant TZ
        this.modified = modified ? DateTime.fromISO(modified) : null;  // not required
        this.sm_state = sm_state;  // required 
        this.owner_id = owner_id;
        this.owner = owner ?? {id: owner_id};
        this.vendor_id = vendor_id;
        this.vendor = vendor ?? {id: vendor_id};
        this.metadata = metadata ?? {};
        this.custom_data = custom_data ?? {};
        this.urls = urls ?? {};
        this.workflow_run_number = workflow_run_number;
        this.workflow_spec_id = workflow_spec_id;
        this.issue_count = 1;  // hard-code this for easier aggregation in ag-grid
        Object.entries(others).forEach(([k, v]) => {
            this[k] = v;
        });
        // then add any extra ones
        // we will add 'bin keys' for time-related bins for this item
        // bin keys should all be sortable -- either numbers? or strings?
        const binDate = this.created_as_tenant_tz;  // pick the date to use to make bins -- makes it required!
        this.binDate = binDate;  // add it as instance property too
        const binTypes = [YearBin, QuarterBin, MonthBin, WeekBin, DayBin];
        // instead of hard-coding the bin properties here, use static method on each Bin class that has the appropriate itemBinProperty name to use
        // then its only defined in ONE place
        binTypes.forEach(bt => {
            this[bt.itemBinProperty] = bt.getBinKeyFromDateTime(binDate);
        })
    }
}


const defaultAllowedSelectFilters = [
    'vendor_id', 'owner_id', 'sm_state', 'metadata.defect_found_at', 
    'metadata.issue_defect', 'metadata.category', 'metadata.rank_obj', 'metadata.severity_obj', 
    //'metadata.sort_responsible_party', 
    'metadata.obtainSamples', 'metadata.containmentReport', 'metadata.temporaryQStd', 'metadata.issueRma', 'metadata.issueNonCon', 
    //'metadata.part_identification_responsible_party', 'metadata.container_identification_responsible_party', 
    //'metadata.issue_location', 'metadata.issue_type',
    'metadata.issue_defect_enum_id',
];
const knownNestedObjectSelectFilters = [
    'metadata.rank_obj', 'metadata.severity_obj', 'metadata.nuisance_obj',
];

const defaultDisallowedSelectFilters = [
    'metadata.sort_responsible_party', 
    'metadata.part_identification_responsible_party', 'metadata.container_identification_responsible_party', 
    'metadata.issue_location', 'metadata.issue_type',
]


export default {
    name: 'QualityAnalytics',
    components: {
        AnalyticsChart,
        Calendar,
        AnalyticsSelectFilter,
    },
    mixins: [
        errorHandling,
        tenantInfoMixin,
    ],
    props: {
        // for now this component is hard-coded to only get quality workflow stuff via the endpoints it uses
        // but later, you should add props that allow changing what data/workflows/objects it gets
        // use your generic analytics app on backend
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            // WorkflowSpec ID / options - loading items for one at a time
            loadingSpecOptions: false,
            availableWfSpecs: [],  // get from API on mounted()
            selectedWfSpecId: null,  // the ID of the workflow spec we want to use for analytics
            // Analytics settings themselves
            workflowItemName: '', // can get from API
            chartDefs: [],  // get from API - Array of objects
            allowedSelectFilters: [],  // array of strings for field keys that we'll allow as selectFilters 
            // TODO - make another allowed list for allowed in charts
            // LIST OF ITEMS
            loadingItems: false, // when loading items
            items: [],  // array of AnalyticsItem instances
            itemsFiltered: [],  // AFTER ALL FILTERS
            loadingFields: false,  // when loading fieldList 
            fieldList: [],  // array of field data from backend
            // DATE BINS - make them match the structure of your selectFields -- they're basically the same
            // each of these should have { key, label, options[], options_map{}, }
            yearField: {},
            quarterField: {},
            monthField: {},
            weekField: {},
            dayField: {},
            // DATE FILTER
            dateRangeMode: 'day',  // should be 'day' or 'month' - all other day/date/month settings are based on this
            datePickerView: '',  // should be 'date' or 'month' or 'year' for PrimeVue Calendar - set when we set other min/max dates so its all together
            datePickerFormat: '',  // change based on dateRangeMode - set when we set other things
            overallMinDate: null,  // set based on 'day' or 'month' dateRangeMode - not computed, so we can be precise about updating it
            overallMaxDate: null,  
            localStartDate: null,
            localEndDate: null,
            // SELECT FILTERS
            selectFilters: {},  // the current values / filters for ALL select filters
            // keys are the field on 'item' that should be filtered against, values are arrays of the selected IDs/values from the filter
            // use this.$set() to update selectFilters when there are changes
            // later, you could change structure of selectFilters to add something for 'all' or 'any' -- do items need to have all selections?  or just one of them
            // DETAIL MODAL
            detailModalShowing: false,
            detailItemIds: [],  // array of IDs to show?  then we use this computed to return array of full items
            detailTitle: '',
            detailCurrentPage: 1, // PAGINATION - starting from 1
            detailPerPage: 25,  // not changeable by user for now
            detailTableFields: [  // TOOD - change which cols are used here based on analytics item type
                {
                    key: 'title',
                    label: 'Title',
                    sortable: true,
                },
                {
                    key: 'vendor',
                    label: 'Supplier',
                    sortable: true,
                    sortByFormatted: true,
                    formatter: (value, key, item) => {
                        return value.name ?? item.vendor_name;
                    },
                },
                {
                    key: 'created_as_tenant_tz',
                    label: 'Date',
                    sortable: true,
                    formatter: (value) => {
                        return FormatDateHelper(value);
                    },
                },
            ],
            combinedData: null,  // TESTING
        }
    },
    computed: {
        hideDwfQuality() {
            const isHidden = this.$getTenantFlag('hide_dwfrun_quality', false); // some tenants might not have flag at all
            return isHidden;
        },
        hideNewQuality() {
            const isShown = this.$getTenantFlag('show_new_quality', false);  // some tenants might not have flag at all
            return !isShown;
        },
        hasMultipleWfSpecs() {
            // if we have more than one workflow spec available, then we can show the select
            return this.availableWfSpecs.length > 1;
        },
        isNewQualityIssue() {
            return this.selectedWfSpecId === 'QualityIssue';
        },
        loadingResults() {
            return this.loadingItems || this.loadingFields;
        },
        hasSelectedWfSpec() {
            return !!this.selectedWfSpecId;
        },
        hasItems() {
            return !!this.items && Array.isArray(this.items) && this.items.length;
        },
        detailItems() {  // items to show in detail modal
            return this.items.filter(item => this.detailItemIds.includes(item.id));
        },
        showingDetailItemsStart() {  // starting number for currently displayed items (1-based)
            return (this.detailCurrentPage - 1) * this.detailPerPage + 1;
        },
        showingDetailItemsEnd() {  // ending number for currently displayed items (1-based)
            const maxItem = this.detailCurrentPage * this.detailPerPage;
            return Math.min(this.detailItems.length, maxItem);
        },
        selectFields() {
            // array of your own column objects that have type='select'
            // each one will have { key, label, type, options, options_map }
            return this.fieldList.filter(fld => fld.type === 'select');
            //return [];
        },
        selectFieldKeys() {
            // all the 'key' from selectFields 
            return this.selectFields.map(fld => fld.key);
        },
        selectFieldsForFilters() {
            // TODO - pass in a list of allowed filter fields in props -- a subset of selectFields so we can hide things that don't make sense or are not important
            // NO LONGER NEEDED I BELIEVE return this.selectFields.filter(fld => this.allowedSelectFilters.includes(fld.key));  // just return this for now
            // just use ones that actually have options for now
            const selectFields = this.selectFields.filter(fld => {
                const notHidden = !fld.hide_filter;
                const hasOptions = !!fld.options && Array.isArray(fld.options) && fld.options.length;
                const notDisallowed = !this.disallowedSelectFilters.includes(fld.key);
                return notHidden && hasOptions && notDisallowed;
            });
            // Add boolean fields as select filters with Yes/No
            const booleanSelectFields = this.booleanFieldsForFilters.map(fld => ({
                key: fld.key,
                label: fld.label,
                type: 'select',
                options: [
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' }
                ],
                options_map: { true: 'Yes', false: 'No' }
            }));
            return [...selectFields, ...booleanSelectFields];
        },
        fieldKeys() {
            // all the 'key' from fieldList 
            return this.fieldList.map(fld => fld.key);
        },
        fieldListForCharts() {
            // take the standard fieldList AND the fields we built up for year, quarter, month, week -- so they share same format
            return [...this.fieldList, this.yearField, this.quarterField, this.monthField, this.weekField, this.dayField];
        },
        booleanFields() {
            // all boolean fields from fieldList
            return this.fieldList.filter(fld => fld.type === 'boolean');
        },
        booleanFieldsForFilters() {
            // boolean fields that are not hidden and not disallowed
            return this.booleanFields.filter(fld => {
                const notHidden = !fld.hide_filter;
                const notDisallowed = !this.disallowedSelectFilters.includes(fld.key);
                return notHidden && notDisallowed;
            });
        },
        isAnySelectFilterApplied() {
            // run through this.selectFilters object, see if there are any with non-falsey values (if value is null, then filter is not applied)
            return Object.values(this.selectFilters).some(selection => !!selection);  // if any have non-falsey selections, then there is filter applied
        },
        isStartDateFilterApplied() {
            // Boolean, is localStartDate possibly filtering anything?
            return this.localStartDate > this.overallMinDate;
        },
        isEndDateFilterApplied() {
            // Boolean, is localEndDate possibly filtering anything?
            return this.localEndDate < this.overallMaxDate;
        },
        isAnyDateFilterApplied() {
            return this.isStartDateFilterApplied || this.isEndDateFilterApplied;
        },
        isAnyFilterApplied() {
            // is ANY filter active, of any type?
            return this.isAnySelectFilterApplied || this.isStartDateFilterApplied || this.isEndDateFilterApplied;
        },
        isResetButtonDisabled() {  // the OVERALL reset button for all filters
            return this.loadingResults || !this.isAnyFilterApplied;
        },
    },
    mounted() {
        this.getAnalyticsSettings();
        this.getAvailableWfSpecs();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getAnalyticsSettings() {
            // MAKE THIS API CALL LATER 
            this.workflowItemName = 'Quality Issues';
            this.allowedSelectFilters = defaultAllowedSelectFilters;
            this.disallowedSelectFilters = defaultDisallowedSelectFilters;
            this.chartDefs = [];  // changed to do it dynamically
        },
        async getAvailableWfSpecs() {
            // get the list of WF specs that you can get analytics for 
            this.loadingSpecOptions = true;
            try {
                // const optionsResp = await suppliosApi.qualityAnalytics.list();  // just a list of { value, label }
                // NEW call .list_options() instead, which includes new QualityIssue class
                const optionsResp = await suppliosApi.qualityAnalytics.list_options();
                const rawAvailableWfSpecs = optionsResp.data;  // we filter out the old DWF ones if we have flag set to hide them
                // this is super hacky... but this entire component should get nuked soon anyway
                console.log(`rawAvailableWfSpecs`, rawAvailableWfSpecs);
                let availableWfSpecs = [...rawAvailableWfSpecs];
                if (this.hideDwfQuality) {
                    // the options here will be either { value: 'QualityIssue', label: 'Quality Issue' } (that's new Quality) or { value: 'some_dwf_spec_id', label: 'Supplier Quality Issue' }
                    // only allow the new QualityIssue one
                    availableWfSpecs = availableWfSpecs.filter(wfSpec => wfSpec.value === 'QualityIssue');
                }
                if (this.hideNewQuality) {
                    // filter OUT the new QualityIssue one
                    availableWfSpecs = availableWfSpecs.filter(wfSpec => wfSpec.value !== 'QualityIssue');
                }
                this.availableWfSpecs = availableWfSpecs;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingSpecOptions = false;
            // if we only have ONE, then select it here, selector will be hidden
            if (this.availableWfSpecs.length === 1) {
                const wfSpec = this.availableWfSpecs[0];
                this.handleWfSpecSelection(wfSpec.value);
            }
        },
        handleWfSpecSelection(wfSpecId) {
            this.selectedWfSpecId = wfSpecId;  // might be 'QualityIssue' - then we fetch differently 
            this.getAnalytics();
        },
        async getCombined(wfSpecId) {
            this.loadingItems = true;
            this.loadingFields = true;
            try {
                const combinedResp = await suppliosApi.qualityAnalytics.get_combined(wfSpecId);
                this.combinedData = combinedResp.data;  // FOR DEBUG
                const { items, columns } = combinedResp.data;
                // SET FIELDS / COLUMNS
                this.fieldList = columns;
                this.setChartDefs();
                // SET ITEMS
                const tenant_tz_name = this.tenantInfo.settings.timezone || 'UTC';
                this.items = items.map(wfRun => new AnalyticsItem({...wfRun, tenant_tz_name }));
                this.setInitialLoadData();
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingItems = false;
            this.loadingFields = false;
        },
        async getAnalytics() {
            // fetch items and columns/fields when you selet a workflow spec ID to use
            if (!this.selectedWfSpecId) {
                console.warn(`No wfSpecId -- so NOT fetching analytics data`);
                return;
            }
            // NEW WAY (not yet)
            //await this.getCombined(this.selectedWfSpecId);
            // OLD WAY
            this.getFields(this.selectedWfSpecId);
            this.getItems(this.selectedWfSpecId);
        },
        async getItems(wfSpecId) {
            this.loadingItems = true;
            try {
                // check if we are getting old DWF items, or new QualityIssue items
                if (this.isNewQualityIssue) {
                    // for new QualityIssue we use same endpoints as filter_list view
                    const { data } = await suppliosApi.qualityIssue.get_filter_list();
                    // get_filter_list has meta/columns in there too... but as long as we have separate methods here we'll get items/columns separate... can change later after you merge all quality
                    const tenant_tz_name = this.tenantInfo.settings.timezone || 'UTC';
                    this.items = data.items.map(qi => new AnalyticsItem({...qi, tenant_tz_name }));
                    this.setInitialLoadData();
                } else {
                    const { data } = await suppliosApi.qualityAnalytics.get_items(wfSpecId);
                    const tenant_tz_name = this.tenantInfo.settings.timezone || 'UTC';
                    this.items = data.map(wfRun => new AnalyticsItem({...wfRun, tenant_tz_name }));
                    this.setInitialLoadData();
                    //this.makeBins(); - not needed - will happen at the end of setInitialLoadData
                    //this.setItemsFiltered(); - not needed - will happen at the end of setInitialLoadData
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingItems = false;
        },
        setChartDefs() {
            // call this once you have items/fields/cols set 
            const chartDefs = [];
            const maxChartCount = 8;
            const shouldAddFieldKey = (fieldKey) => {
                return chartDefs.length < maxChartCount && chartDefs.every(cd => cd.seriesKey !== fieldKey);
            }
            // first ones - for either new or old analytics 
            if (this.fieldKeys.includes('sm_state')) {
                chartDefs.push({
                    chartKey: 'bar',
                    xKey: '',
                    seriesKey: 'sm_state',
                    height: 400,
                })
            }
            if (this.isNewQualityIssue) {
                // for NEW QualityIssue 
                // this is totally hacky to hardcode these.... but this entire component is going to be replaced soon anyway
                if (this.fieldKeys.includes('_cf_data.rank')) {
                    chartDefs.push({
                        chartKey: 'stacked_bar',
                        xKey: 'dateBins_month',
                        seriesKey: '_cf_data.rank',
                        height: 400,
                    })
                }
                if (this.fieldKeys.includes('_cf_data.severity')) {
                    chartDefs.push({
                        chartKey: 'stacked_bar',
                        xKey: 'dateBins_month',
                        seriesKey: '_cf_data.severity',
                        height: 400,
                    })
                }
                if (this.fieldKeys.includes('vendor_id')) {
                    chartDefs.push({
                        chartKey: 'pie',
                        xKey: '',
                        seriesKey: 'vendor_id',
                        height: 400,
                    })
                }
                // more added April 28 2025 for P-company, but safe to add for all
                if (this.selectFieldKeys.includes('_cf_data.issue.plant')) {
                    chartDefs.push({
                        chartKey: 'stacked_bar',
                        xKey: 'dateBins_month',
                        seriesKey: '_cf_data.issue.plant',
                        height: 400,
                    });
                    chartDefs.push({
                        chartKey: 'pie',
                        xKey: '',
                        seriesKey: '_cf_data.issue.plant',
                        height: 400,
                    });
                }
                // then add some extra pie charts for whatever select fields are left
                this.selectFieldKeys.forEach(fKey => {
                    if (shouldAddFieldKey(fKey)) {
                        chartDefs.push({
                            chartKey: 'pie',
                            xKey: '',
                            seriesKey: fKey,
                            height: 400,
                        });
                    }
                });
            } else {
                // for OLD DWF types 
                if (this.fieldKeys.includes('rank_obj') || this.fieldKeys.includes('metadata.rank_obj')) {
                    chartDefs.push({
                        chartKey: 'stacked_bar',
                        xKey: 'dateBins_month',
                        seriesKey: this.fieldKeys.includes('rank_obj') ? 'rank_obj' : 'metadata.rank_obj',
                        height: 400,
                    })
                }
                if (this.fieldKeys.includes('severity_obj') || this.fieldKeys.includes('metadata.severity_obj')) {
                    chartDefs.push({
                        chartKey: 'stacked_bar',
                        xKey: 'dateBins_month',
                        seriesKey: this.fieldKeys.includes('severity_obj') ? 'severity_obj' : 'metadata.severity_obj',
                        height: 400,
                    })
                }
                if (this.fieldKeys.includes('vendor_id')) {
                    chartDefs.push({
                        chartKey: 'pie',
                        xKey: '',
                        seriesKey: 'vendor_id',
                        height: 400,
                    })
                }
            }
            this.chartDefs = [...chartDefs];
        },
        setInitialLoadData() {
            // set things that should only be set on initial load
            const allDateTimes = this.items.map(item => item.created).filter(dt => DateTime.isDateTime(dt));
            // use UTC 'created' here -- we will adjust further down in this method
            const tenant_tz_name = this.tenantInfo.settings.timezone || 'UTC';
            console.log('dateRangeMode is', this.dateRangeMode);
            switch (this.dateRangeMode) {
                case 'day':
                    this.datePickerView = 'date';
                    this.datePickerFormat = 'M d yy';
                    break;
                case 'month':
                    this.datePickerView = 'month';
                    this.datePickerFormat = 'MM yy';
                    break;
                case 'year':
                    this.datePickerView = 'year';
                    this.datePickerFormat = 'yy';
                    break;
                default:
                    break;
            }
            const startUtc = DateTime.min(...allDateTimes) ?? DateTime.utc();  // if there were no items, then DateTime.min() returns undefined
            console.log('startUtc', startUtc);
            this.overallMinDate = startUtc.setZone(tenant_tz_name).startOf(this.dateRangeMode).toJSDate();  // adjust to tenant TZ... but toJSDate will convert it back to local TZ.... so datepickers will not reflect correct start/end.... ???
            const endUtc = DateTime.max(...allDateTimes) ?? DateTime.utc();  // if there were no items, then DateTime.max() returns undefined
            console.log('endUtc', endUtc);
            this.overallMaxDate = endUtc.setZone(tenant_tz_name).endOf(this.dateRangeMode).toJSDate();  // adjust to tenant TZ
            this.resetAllFilters();
        },
        makeBins() {
            // make sure you have this.items, then find start/end of range, the make bins
            // NEW - just get it from the date picker... we already set min/max dates for date picker based on earliest/latest possible dates in all issues list
            const start = DateTime.fromJSDate(this.localStartDate);
            const end = DateTime.fromJSDate(this.localEndDate);
            // TODO - deal with timezones?  not sure they are correct...
            const yearBins = makeYearBins(start, end);
            const quarterBins = makeQuarterBins(start, end);
            const monthBins = makeMonthBins(start, end);
            const weekBins = makeWeekBins(start, end);
            const dayBins = makeDayBins(start, end);
            this.yearField = {
                key: yearBins[0].itemBinProperty, // 'dateBins.year',
                label: 'Year',
                type: 'select',
                options: yearBins.map(yb => ({value: yb.binKey, label: yb.binName})),
                options_map: Object.fromEntries(yearBins.map(yb => ([yb.binKey, yb.binName]))),
            };
            this.quarterField = {
                key: quarterBins[0].itemBinProperty, // 'dateBins.quarter',
                label: 'Quarter',
                type: 'select',
                options: quarterBins.map(bin => ({value: bin.binKey, label: bin.binName})),
                options_map: Object.fromEntries(quarterBins.map(bin => ([bin.binKey, bin.binName]))),
            };
            this.monthField = {
                key: monthBins[0].itemBinProperty, // 'dateBins.month',
                label: 'Month',
                type: 'select',
                options: monthBins.map(bin => ({value: bin.binKey, label: bin.binName})),
                options_map: Object.fromEntries(monthBins.map(bin => ([bin.binKey, bin.binName]))),
            };
            this.weekField = {
                key: weekBins[0].itemBinProperty, // 'dateBins.week',
                label: 'Week',
                type: 'select',
                options: weekBins.map(bin => ({value: bin.binKey, label: bin.binName})),
                options_map: Object.fromEntries(weekBins.map(bin => ([bin.binKey, bin.binName]))),
            };
            this.dayField = {
                key: dayBins[0].itemBinProperty,
                label: 'Day',
                type: 'select',
                options: dayBins.map(bin => ({value: bin.binKey, label: bin.binName})),
                options_map: Object.fromEntries(dayBins.map(bin => ([bin.binKey, bin.binName]))),
            };
            /* 
            // split interval into smaller intervals?
            // this works very well, handles shorter months correctly, etc.... but maybe not what you need...
            const interval = Interval.fromDateTimes(start, end);
            const oneMonth = Duration.fromObject({months: 1});
            const monthBins = interval.splitBy(oneMonth);  // splits into array of Intervals, each with 'start' and 'end' DateTime
            console.log('monthBins below');
            monthBins.forEach(mb => {
                console.log(`${mb.start.toLocaleString()} -> ${mb.end.toLocaleString()}`);
            }); */
        },
        async getFields(wfSpecId) {
            this.loadingFields = true;
            try {
                if (this.isNewQualityIssue) {
                    // for new QualityIssue we use same endpoints as filter_list view
                    const { data } = await suppliosApi.qualityIssue.get_analytics_columns();
                    this.fieldList = data;
                    this.setChartDefs();
                } else {
                    const { data } = await suppliosApi.qualityAnalytics.get_columns(wfSpecId);
                    this.fieldList = data;
                    this.setChartDefs();
                    //this.fieldMap = Object.fromEntries(data.map(fld => ([fld.key, fld])));
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingFields = false;
        },
        resetAllFilters() {
            // set initial filter values 
            this.resetDateFilters(false);  // pass false to not apply the changes yet - we will do once at the end
            const selectFilterComponents = this.getAllSelectFilters();
            //console.log('from resetAllFilters - selectFilterComponents', selectFilterComponents);
            selectFilterComponents.forEach(comp => comp.reset(true));  // true makes them skip emitting update, so we have to clear this.selectFilters ourselves!
            this.selectFilters = {};  // must clear this ourselves because we told child filter components to NOT send their events
            this.applyUpdatedFiltersAndDates();  /// this updates filters too
        },
        setItemsFiltered() {
            // DO THE FILTERS
            console.log('running setItemsFiltered');
            const filterConditions = [];  // make array of callback functions 
            if (this.localStartDate) {
                const gteStartDate = (item) => item.binDate >= DateTime.fromJSDate(this.localStartDate);
                filterConditions.push(gteStartDate);
            }
            if (this.localEndDate) {
                const lteEndDate = (item) => item.binDate <= DateTime.fromJSDate(this.localEndDate);
                filterConditions.push(lteEndDate);
            }
            // MAKE SELECT FILTERS - ONLY WORKS FOR SINGLE-SELECT
            Object.entries(this.selectFilters).forEach(([filterKey, selections]) => {
                // filterKey is the thing to filter on in 'items' -- EXCEPT IF YOU HAVE NESTED OBJECTS 
                // For boolean fields, selections will be [true] or [false]
                // For select fields, selections is array of values
                if (!(selections && selections.length > 0)) {
                    return;
                }
                // Check if this is a boolean field
                const isBooleanField = this.booleanFieldsForFilters.some(fld => fld.key === filterKey);
                if (isBooleanField) {
                    // Boolean: filter for exact true/false match
                    const filterCallback = (item) => selections.includes(get(item, filterKey) ?? null);
                    filterConditions.push(filterCallback);
                } else {
                    // Dec 15 2022 - check if filterKey points to a nested object.... if so, assume that it has 'value' key inside, and that's what we actually want to filter on
                // but introspecting the first object is very unreliable... what if values are sparse and first item doesn't have value for this filter... 
                // this is why you need to know the exact schema for all fields!!!
                // for now, we'll make a total hack and just check if its one of our known nested objects (rank_obj, severity_obj, nuisance_obj)
                    const finalFilterKey = knownNestedObjectSelectFilters.includes(filterKey) ? `${filterKey}.value` : filterKey;
                    // selections is the ARRAY of selected items/values that item will have
                    // OR... selections can be 'null' if we do not want to filter on that key at all
                    const filterCallback = (item) => (selections.includes(get(item, finalFilterKey)));
                    filterConditions.push(filterCallback);
                }
            })
            this.itemsFiltered = this.items.filter(item => {
                return filterConditions.every(cond => cond(item));
            });
        },
        resetDateFilters(apply = false) {  // apply - should we actually APPLY the new filters yet? re-filter items to itemsFiltered? - set true if you are only resetting dates and want to apply the change, leave as false if you will call something later to apply the change
            // call from button to perform the reset of date filters only
            this.localStartDate = this.overallMinDate;
            this.localEndDate = this.overallMaxDate;
            if (apply) {
                this.applyUpdatedFiltersAndDates();  /// this updates filters too, but they will all still be applied if applied before this
            }
        },
        handleStartDateSelect(selectedDate) {
            // we are NOT using v-model, so we can set start/end of month/day - payload is JS Date
            // also must call this.applyUpdatedFiltersAndDates() when they are changed
            const localStartDateLuxon = DateTime.fromJSDate(selectedDate).startOf(this.dateRangeMode);
            this.localStartDate = localStartDateLuxon.toJSDate();
            // check this.localEndDate -- if it is BEFORE new start date, set it to end of period automatically
            if (!!this.localEndDate && (this.localEndDate < this.localStartDate)) {
                // just pass the exact same JS date to end date handler... it will get shifted to END of period
                this.handleEndDateSelect(this.localStartDate);
            }
            this.applyUpdatedFiltersAndDates();
        },
        handleEndDateSelect(selectedDate) {
            // we are NOT using v-model, so we can set start/end of month/day - payload is JS Date
            // also must call this.applyUpdatedFiltersAndDates() when they are changed
            const localEndDateLuxon = DateTime.fromJSDate(selectedDate).endOf(this.dateRangeMode);
            this.localEndDate = localEndDateLuxon.toJSDate();
            // check this.localStartDate -- if it is AFTER new end date, set it to start of period automatically
            if (!!this.localStartDate && (this.localEndDate < this.localStartDate)) {
                // just pass the exact same JS date to start date handler... it will get shifted to START of period
                this.handleStartDateSelect(this.localEndDate);
            }
            this.applyUpdatedFiltersAndDates();
        },
        applyUpdatedFiltersAndDates() {
            // when dates are updated, we have to make new bins and re-run filters
            this.makeBins();
            this.setItemsFiltered();
        },
        applyUpdatedFilters() {
            // call this when dates are NOT changed, but filters are changed
            // we will not make new bins, but still setItemsFiltered
            this.setItemsFiltered();
        },
        handleChartShowDetail(showDetailParams) {
            // payload is object with { title, itemIds[] }
            const { itemIds, title } = showDetailParams;
            if (!itemIds || !Array.isArray(itemIds) || itemIds.length < 1) {
                return;
            }
            this.detailItemIds = itemIds;
            this.detailTitle = `${title}`;  // just title for now... can add more later
            this.detailModalShowing = true;
            // doesn't matter if we get from item or itemsFiltered -- because we have specific IDs to get... but items is safer
            // put in computed: const itemsToShow = this.items.filter(item => itemIds.includes(item.id));
        },
        handleDetailModalHidden() {
            // reset things about the detail modal
            this.detailItemIds = [];
            this.detailTitle = '';
            this.detailCurrentPage = 1;
        },
        getAllSelectFilters() {
            // get all the select filter components so you can call things on them like .reset()
            // using $refs -- for ref to return a Component instance, it needs to be on a vue component - if used on a normal DOM element, it returns the DOM element
            // we are using ref in a v-for, which forces it to be an array
            return this.$refs['selectFilters'] ?? [];
        },
        handleSelectFilterUpdated(fieldKey, selections) {
            // fieldKey is the key that the select filter is filtering on
            // selections are the values/ids that are selected
            // use this.$set() to update this.selectFilters -- which is used to actually run the filters
            console.log('select filter updated', fieldKey, selections);
            this.$set(this.selectFilters, fieldKey, selections);
            this.applyUpdatedFilters();
        },
        handleSelectFilterReset(fieldKey) {
            // similar to handleSelectFilterUpdated - but we will REMOVE THE FILTER COMPLETELY - better performance
            console.log('select filter RESET', fieldKey);
            this.$set(this.selectFilters, fieldKey, null);  // setting it to null removes the filter completely (see your logic above)
            this.applyUpdatedFilters();
        },
        isSelectFilterApplied(fieldKey) {
            // for checking a SPECIFIC select filter -- you have computed for checking if ANY are applied
            // return Boolean - true if this is in this.selectFilters AND has values set
            return !!this.selectFilters[fieldKey];
        },
    }
}
</script>

<style lang="scss" scoped>

.analytics-select-filter-toggle-button.collapsed .when-open,
.analytics-select-filter-toggle-button.not-collapsed .when-closed {
  display: none;
}

.analytics-select-filter-wrapper {
    background-color: $gray-100;
    .analytics-select-filter-toggle-button {
        background-color: $gray-100 !important;
        &:hover {
            background-color: $gray-200 !important;
        }
    }
    &.filter-applied {
        background-color: $accent-50 !important;  // for date filter area
        .analytics-select-filter-toggle-button {  // for buttons on select filter collapse toggles
            background-color: $accent-50 !important;
            &:hover {
                background-color: $accent-100 !important;
            }
        }
    }
}

</style>